<template>
  <v-form v-model="valid">
    <div
      :class="$vuetify.breakpoint.xs ? 'fin-confirm-dialog-content-small pa-3' : 'fin-confirm-dialog-content pa-3'">
      <v-card-text class="pa-0">
        <p v-if="!createNew" class="change-intro">Hier kannst du die Details dieser Transaktion bearbeiten.</p>
        <p v-else class="change-intro">Hier kannst du eine neue Transaktion erstellen.</p>

        <v-row v-if="model.transaction === 'SELL' || model.transaction === 'BUY'"
               class="mt-0">
          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Stückzahl</p>
            <v-text-field v-model="model.count"
                          :rules="editCountNumberRules"
                          filled

            />
          </v-col>

          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Kurs in Euro</p>
            <fin-currency-field v-model="editRealizedPrice"
                                :error-messages="editRealizedPrice <= 0 ? 'Bitte eine Zahl >0 eingeben' : '' "
                                required
                                width="100%"
            />
          </v-col>

          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Gebühr in Euro</p>
            <fin-currency-field v-model="editRealizedFees"
                                required
                                width="100%"
            />
          </v-col>

          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Datum</p>
            <fin-date-picker v-model="editDate"
                             :max-date="model.transaction !== 'SELL' && isOldestEntry ? firstSellDate : new Date()"
                             :min-date="model.transaction === 'SELL' ? firstBuyDate : model.instrument.firstPriceDate ? new Date(model.instrument.firstPriceDate) : undefined"
                             time-picker
                             @input="val => editDate = val"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="model.transaction === 'CASH_PAY_IN' || model.transaction === 'CASH_PAY_OUT'"
          class="mt-0">
          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Betrag in Euro</p>
            <fin-currency-field v-model="model.cashAmount"
                                :error-messages="model.cashAmount <= 0 ? 'Bitte eine Zahl >0 eingeben' : ''"
                                required
                                width="100%"
            />

          </v-col>

          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Datum</p>
            <fin-date-picker v-model="editDate"
                             time-picker
                             @input="val => editDate = val"
            />
          </v-col>
        </v-row>

        <v-row v-if="model.transaction === 'DIVIDEND'"
               class="mt-0">
          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Dividende pro Aktie in Euro</p>
            <fin-currency-field v-model="model.dps"
                                :error-messages="model.dps <= 0 ? 'Bitte eine Zahl >0 eingeben' : ''"
                                required
                                width="100%"
            />

          </v-col>

          <v-col class="py-0" cols="12" sm="6">
            <p class="change-field-label mb-2">Datum</p>
            <fin-date-picker v-model="editDate"
                             time-picker
                             :min-date="firstBuyDate"
                             @input="val => editDate = val"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions :class="' d-flex justify-end px-0 '+($vuetify.breakpoint.xs ? 'mb-8' : ' ')">
        <v-btn outlined
               @click="cancel"
        >
            <span class="pl-4 pr-4">
              Zurück
            </span>
        </v-btn>
        <v-btn
          :disabled="!valid"
          dark
          @click="createNew ? createDividend() : saveTransaction()">
            <span class="pl-4 pr-4">
              Speichern
            </span>
        </v-btn>
      </v-card-actions>
    </div>
  </v-form>
</template>

<script>
import FinDatePicker from "@/components/common/DatePicker"
import FinCurrencyField from "@/components/common/CurrencyField"
import {finPortfolioAPI} from "@/js/finAPI"
import {currencyFilter} from "@/plugins/fincraft"

export default {
  name: "FinEditTransactionForm",
  components: {FinCurrencyField, FinDatePicker},
  props: {
    value: {
      type: Object,
      default: undefined
    },
    /** Has to be set if a new transaction should be created, instead of editing an existing one */
    createNew: {
      type: Boolean,
      default: false
    },
    /** Shows if the selected transaction is the last entry (sorted by creation date) in the list of transactions */
    isOldestEntry: {
      type: Boolean,
      default: false
    },
    /** The date on which the instrument has been bought for first time */
    firstBuyDate: {
      type: Date,
      default: undefined
    },
    /** The date on which the instrument has been sold for the first time */
    firstSellDate: {
      type: Date,
      default: undefined
    }
  },
  data() {
    return {
      model: this.value,
      valid: false,
      editCountNumberRules: [
        v => !!v || 'Bitte eine Zahl eingeben',
        v => {
          // Workaround to exclude exponential notation
          v = v.toString()
          if (v.includes("e")) {
            return 'Bitte eine Zahl eingeben'
          } else {
            return true
          }
        },
        v => !Number.isNaN(v) || 'Bitte eine Zahl eingeben',
        v => v > 0 || 'Bitte eine Zahl >0 eingeben'
      ]
    }
  },
  computed: {
    editRealizedPrice: {
      get() {
        return Number(this.model.realizedPrice || this.model.realizedPrice === 0 ? this.model.realizedPrice.toFixed(2) : this.model.planedPrice.toFixed(2))
      },
      set(val) {
        this.model.realizedPrice = val
      }
    },
    editDate: {
      get() {
        return new Date(this.model.realizedTimestamp ? this.model.realizedTimestamp : this.model.actionTimestamp)
      },
      set(val) {
        this.model.realizedTimestamp = val
      }
    },
    editRealizedFees: {
      get() {
        return this.model.realizedFees || this.model.realizedFees === 0 ? this.model.realizedFees : this.model.planedFees
      },
      set(val) {
        this.model.realizedFees = val
      }
    }
  },
  methods: {
    async addCashAndEditTransaction(cashDeficit, model) {
      console.log("calling cashPayIn", model)
      await finPortfolioAPI.cashPayIn(model.portfolioId, cashDeficit, model.realizedTimestamp)
        .then(data => {
          console.log("calling transactionEdit", model)
          this.saveTransaction(true)
        })
        .catch(error => {
          console.error("Failed to update cash and transaction", error)
        })
    },
    async saveTransaction(secondTry = false) {
      try {
        const portfolioId = this.model.portfolioId

        const resp = await finPortfolioAPI.transactionEdit(portfolioId, this.model.actionTimestamp,
          this.model.transaction, this.model.count, this.model.realizedPrice,
          this.model.realizedFees, this.model.realizedTimestamp, this.model.cashAmount, this.model.dps)

        if (resp.isValid) {
          this.$emit('updatePortfolio', this.model)
          this.$toasted.global.finToast({
            message: 'Deine Transaktion wurde erfolgreich gespeichert.',
            type: 'success'
          })
        } else {
          // show a dialog, so that the user can confirm if he wants to add the missing cash
          if (!secondTry && resp.cashDeficit && resp.cause === "Die Transaktion verursacht einen negativen Cash!") {
            this.$confirm({
              title: 'Dein Cashbestand zum Kaufzeitpunkt am ' + this.$options.filters.datetime(this.editDate) + ' reicht nicht zum Erwerb der Position!',
              text: 'Möchtest du den Cashbestand zum ' + this.$options.filters.datetime(this.editDate) + ' um den Fehlbetrag von ' + currencyFilter(resp.cashDeficit) + ' erhöhen?',
              confirmBtn: 'Cash erhöhen'
            }).then(() => {
              // add cash and call edit transation again
              this.addCashAndEditTransaction(resp.cashDeficit, this.model)
            }).catch((e) => console.error("Failed to add cash and update transaction", e))
          } else if (resp.cashDeficit && resp.cause !== "Die Transaktion verursacht einen negativen Cash!") {
            this.$toasted.global.finToast({
              message: 'Deine Transaktion konnte nicht gespeichert werden. ' + resp.cause,
              type: 'error'
            })
          }
        }

      } catch (e) {
        console.error("Failed to update transaction", e)
        this.$toasted.global.finToast({
          message: 'Deine Transakion konnte nicht gespeichert werden.',
          type: 'error'
        })
      }
    },
    async createDividend() {
      await finPortfolioAPI.transactionDividend(this.model.portfolioId, this.model.instrument.id, this.model.dps, this.model.realizedTimestamp)
        .then(data => {
          this.$emit('updatePortfolio', this.model)
          this.$toasted.global.finToast({
            message: 'Deine Dividende wurde erfolgreich erstellt.',
            type: 'success'
          })
        })
        .catch(error => {
          console.error('Failed to create dividend transaction', error)
          this.$toasted.global.finToast({
            message: 'Deine Dividende konnte nicht erstellt werden.',
            type: 'error'
          })
        })
    },
    cancel() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss" scoped>
.change-intro {
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
}

.change-field-label {
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
}

::v-deep .v-input .v-input__slot {
  box-shadow: $fin-color-shadow;
  border: solid 1px #cfd1d3;
  background-color: #ffffff;
}

::v-deep .transaction-table {
  background-color: #fff !important;
  box-shadow: $fin-color-card-shadow !important;
  border: $fin-color-light-sky-blue solid 1px;
  color: $fin-color_primary-dark;

  thead tr,
  thead th {
    background-color: transparent !important;
  }

}
</style>
